// MapLocationPicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Map, X, MapPin, Maximize, Minimize } from 'lucide-react';
import '../styles/MapLocationPicker.scss';

/**
 * Componente para seleccionar una ubicación en el mapa
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.initialCoordinates - Coordenadas iniciales {latitude, longitude}
 * @param {Function} props.onSelectLocation - Función que se llama al seleccionar una ubicación
 * @param {Function} props.onClose - Función que se llama al cerrar el modal
 */
const MapLocationPicker = ({ initialCoordinates, onSelectLocation, onClose }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(initialCoordinates || {
    latitude: 40.416775,  // Madrid por defecto
    longitude: -3.703790
  });

  // Cargar el script de Google Maps
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    } else {
      setMapLoaded(true);
    }
  }, []);

  // Inicializar el mapa cuando se carga la API
  useEffect(() => {
    if (mapLoaded && mapRef.current && !googleMapRef.current) {
      const mapOptions = {
        center: {
          lat: selectedLocation.latitude,
          lng: selectedLocation.longitude
        },
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true
      };

      // Crear el mapa
      const map = new window.google.maps.Map(mapRef.current, mapOptions);
      googleMapRef.current = map;

      // Crear el marcador
      const marker = new window.google.maps.Marker({
        position: new window.google.maps.LatLng(
          selectedLocation.latitude,
          selectedLocation.longitude
        ),
        map: map,
        draggable: true,
        animation: window.google.maps.Animation.DROP
      });
      markerRef.current = marker;

      // Evento cuando se arrastra el marcador
      marker.addListener('dragend', () => {
        const position = marker.getPosition();
        const newLocation = {
          latitude: position.lat(),
          longitude: position.lng()
        };
        setSelectedLocation(newLocation);
      });

      // Evento cuando se hace clic en el mapa
      map.addListener('click', (event) => {
        marker.setPosition(event.latLng);
        const newLocation = {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng()
        };
        setSelectedLocation(newLocation);
      });

      // Añadir buscador de lugares
      const input = document.getElementById('place-search-input');
      const searchBox = new window.google.maps.places.SearchBox(input);
      
      map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds());
      });

      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces();
        if (places.length === 0) return;

        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;

        // Centrar mapa en el resultado
        map.setCenter(place.geometry.location);
        map.setZoom(17);
        
        // Actualizar marcador
        marker.setPosition(place.geometry.location);
        const newLocation = {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        };
        setSelectedLocation(newLocation);
      });
    }
  }, [mapLoaded, selectedLocation.latitude, selectedLocation.longitude]);

  // Actualizar el mapa cuando cambian las coordenadas iniciales
  useEffect(() => {
    if (mapLoaded && googleMapRef.current && markerRef.current && initialCoordinates) {
      const position = new window.google.maps.LatLng(
        initialCoordinates.latitude,
        initialCoordinates.longitude
      );
      markerRef.current.setPosition(position);
      googleMapRef.current.setCenter(position);
      setSelectedLocation(initialCoordinates);
    }
  }, [initialCoordinates, mapLoaded]);

  // Manejar el cambio de tamaño del mapa
  useEffect(() => {
    if (googleMapRef.current) {
      window.google.maps.event.trigger(googleMapRef.current, 'resize');
    }
  }, [expanded]);

  const handleConfirmLocation = () => {
    onSelectLocation(selectedLocation);
  };

  const toggleMapSize = () => {
    setExpanded(!expanded);
  };

  return (
    <div className={`map-location-picker-overlay ${expanded ? 'expanded' : ''}`}>
      <div className={`map-location-picker ${expanded ? 'expanded' : ''}`}>
        <div className="picker-header">
          <div className="title">
            <Map size={18} className="icon" />
            Seleccionar Ubicación
          </div>
          <div className="actions">
            <button 
              className="btn-icon"
              onClick={toggleMapSize}
              title={expanded ? "Reducir mapa" : "Ampliar mapa"}
            >
              {expanded ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
            <button 
              className="btn-icon"
              onClick={onClose}
              title="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="search-container">
          <input 
            id="place-search-input"
            type="text" 
            placeholder="Buscar ubicación" 
            className="search-input"
          />
        </div>

        <div className="map-container">
          {!mapLoaded ? (
            <div className="map-loading">Cargando mapa...</div>
          ) : (
            <div 
              ref={mapRef} 
              className="google-map"
            ></div>
          )}
        </div>

        <div className="coordinates-display">
          <div className="coordinate">
            <span className="label">Latitud:</span>
            <span className="value">{selectedLocation.latitude}</span>
          </div>
          <div className="coordinate">
            <span className="label">Longitud:</span>
            <span className="value">{selectedLocation.longitude}</span>
          </div>
        </div>

        <div className="picker-footer">
          <button 
            className="btn btn-cancel"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button 
            className="btn btn-confirm"
            onClick={handleConfirmLocation}
          >
            <MapPin size={16} className="icon" />
            Confirmar Ubicación
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapLocationPicker;